import classNames from "classnames";
import { DeleteDialog } from "components";
import { PhosphorTrashButton } from "components/Atoms/PhosphorIconButton";
import TravelClassDropdown from "components/dropdowns/reactselect/TravelClassDropdown";
import { InPageEditingProvider } from "components/MainSectionTemplate/InPageEditingContext";
import { SidePanelContext } from "components/SidePanel/SidePanelContext";
import OptionalTextAreaInput, { OptionalTextAreaDetails } from "components/ValidatedFields/OptionalTextAreaInput";
import OptionalTextInput from "components/ValidatedFields/OptionalTextInput";
import { ContactForExtractorSession } from "extractorSession/list/ContactForExtractorSession";
import { PassengerCardField } from "flights/passenger/PassengerCardField";
import { useValidators } from "formvalidation/hooks";
import React, { useContext, useEffect } from "react";
import { getFlightTravelClass } from "utilities/enumutils";

interface Props {
  passenger: server.dto.ExtractedFlightPassenger
  isInPageEditable: boolean
  onSuccessfulUpdate: () => Promise<any>
}

interface PassengerState {
  contact: server.dto.ContactSmallV2
  passengerName: server.dto.PersonName
  travelClass: server.dto.FlightTravelClass
  seat: string
  bookingCode: string
  meal: string
  eTicket: string
  baggage: string
}

const mapPassengerToState = (p: server.dto.ExtractedFlightPassenger): PassengerState => ({
  contact: p.passengerContact,
  passengerName: p.passengerName,
  travelClass: getFlightTravelClass(p.travelClass),
  seat: p.seat,
  bookingCode: p.bookingReference,
  meal: p.meal,
  eTicket: p.ticketNumber,
  baggage: p.baggage,
});

export const ExtractedPassengerCard = (props: Props) => {
  const [isDeleting, setIsDeleting] = React.useState<boolean>(false);
  const [state, setState] = React.useState<PassengerState>(mapPassengerToState(props.passenger));

  useEffect(() => {
    setState(mapPassengerToState(props.passenger));
  }, [props.passenger]);

  const sidePanelContext = useContext(SidePanelContext);
  const isNarrow = sidePanelContext.layoutType === "narrow";
  const validators = useValidators();

  const onValueChanged = (key: string, value: any) => {
    if (key === "travelClass") {
      setState(prev => ({ ...prev, travelClass: value }));
    } else if (key === "seat") {
      setState(prev => ({ ...prev, seat: value }));
    } else if (key === "bookingCode") {
      setState(prev => ({ ...prev, bookingCode: value }));
    } else if (key === "meal") {
      setState(prev => ({ ...prev, meal: value }));
    } else if (key === "eTicket") {
      setState(prev => ({ ...prev, eTicket: value }));
    } else if (key === "baggage") {
      setState(prev => ({ ...prev, baggage: value }));
    }
  }

  const onDelete = async () => {
    await window.CoreApi.Extractor.DeleteExtractedFlightPassenger({
      extractedFlightPassengerId: props.passenger.extractedFlightPassengerId,
    });
    await props.onSuccessfulUpdate();
  };

  const getUpdateInstruction = (ps: PassengerState): server.dto.UpdateExtractedFlightPassengerInstruction => ({
    extractedFlightPassengerId: props.passenger.extractedFlightPassengerId,
    bookingReference: ps.bookingCode,
    travelClass: ps.travelClass ? ps.travelClass.value : "unknown",
    seat: ps.seat,
    ticketNumber: ps.eTicket,
    meal: ps.meal,
    baggage: ps.baggage,
  });

  return <InPageEditingProvider
    enabled={props.isInPageEditable}
    validate={validators.validateAll}
    onValueChanged={onValueChanged}
    onSuccessfulUpdate={props.onSuccessfulUpdate}
  >
    <div className={classNames("passenger-card-grid", {
      "passenger-card-grid-side-panel-narrow": isNarrow,
    })}>
      <div className="flex gap justify-space-between" style={{ gridArea: "passenger" }}>
        <PassengerCardField
          gridArea=""
          field={{
            key: "contact",
            label: "",
            render: () => <ContactForExtractorSession
              contactSmall={state.contact}
              contactName={state.passengerName}
              extractedFlightPassengerId={props.passenger.extractedFlightPassengerId}
              onSuccessfulUpdate={props.onSuccessfulUpdate}
            />,
          }}
        />
        {(props.isInPageEditable && isNarrow) && <PhosphorTrashButton
          layout="in-square"
          onClick={() => setIsDeleting(true)}
        />}
      </div>
      <PassengerCardField
        gridArea="travelClass"
        field={{
          key: "travelClass",
          label: RESX.Flight.TravelClass,
          isEmpty: !state.travelClass || state.travelClass.value === "unknown",
          render: () => <>{state.travelClass.text}</>,
          inPageEdit: {
            render: ({ value, onChange }) => <TravelClassDropdown
              value={value}
              onChange={v => onChange(v, true)}
              config={{
                focusOnMount: true,
                openMenuOnFocus: true
              }}
            />,
            type: "custom",
            fullWidth: true,
            initialValue: state.travelClass,
            submitChange: (travelClass: server.dto.FlightTravelClass) => {
              return window.CoreApi.Extractor.UpdateExtractedFlightPassenger(
                getUpdateInstruction({ ...state, travelClass })
              )
            }
          }
        }}
      />
      <PassengerCardField
        gridArea="seat"
        field={{
          key: "seat",
          isEmpty: !state.seat,
          label: RESX.Flight.Seat,
          render: () => <>{state.seat}</>,
          inPageEdit: {
            render: ({ value, onChange, submit, cancel }) => <OptionalTextInput
              value={value}
              onChange={v => onChange(v)}
              onEnter={() => submit()}
              onEsc={() => cancel()}
              focusOnMount
              maxLength={serverReference.constants.flight.maxSeatLength}
            />,
            type: "confirmOrCancelButton",
            fullWidth: true,
            initialValue: state.seat,
            submitChange: (seat: string) => {
              return window.CoreApi.Extractor.UpdateExtractedFlightPassenger(
                getUpdateInstruction({ ...state, seat })
              )
            }
          }
        }}
      />
      <PassengerCardField
        gridArea="bookingCode"
        field={{
          key: "bookingCode",
          isEmpty: !state.bookingCode,
          label: RESX.Flight.BookingCode,
          render: () => <>{state.bookingCode}</>,
          inPageEdit: {
            render: ({ value, onChange, submit, cancel }) => <OptionalTextInput
              value={value}
              onChange={v => onChange(v)}
              onEnter={() => submit()}
              onEsc={() => cancel()}
              maxLength={serverReference.constants.flight.maxBookingReferenceLength}
              focusOnMount
            />,
            type: "confirmOrCancelButton",
            fullWidth: true,
            initialValue: state.bookingCode,
            submitChange: (bookingCode: string) => {
              return window.CoreApi.Extractor.UpdateExtractedFlightPassenger(
                getUpdateInstruction({ ...state, bookingCode })
              )
            }
          }
        }}
      />
      <PassengerCardField
        gridArea="meal"
        field={{
          key: "meal",
          isEmpty: !state.meal,
          label: RESX.Flight.Meal,
          render: () => <>{state.meal}</>,
          inPageEdit: {
            render: ({ value, onChange, submit, cancel }) => <OptionalTextInput
              value={value}
              onChange={v => onChange(v)}
              onEnter={() => submit()}
              onEsc={() => cancel()}
              focusOnMount
              maxLength={serverReference.constants.flight.maxMealLength}
            />,
            type: "confirmOrCancelButton",
            fullWidth: true,
            initialValue: state.meal,
            submitChange: (meal: string) => {
              return window.CoreApi.Extractor.UpdateExtractedFlightPassenger(
                getUpdateInstruction({ ...state, meal })
              )
            }
          }
        }}
      />
      <PassengerCardField
        gridArea="eTicket"
        field={{
          key: "eTicket",
          isEmpty: !state.eTicket,
          label: RESX.Flight.ETicket,
          render: () => <>{state.eTicket}</>,
          inPageEdit: {
            render: ({ value, onChange, submit, cancel }) => <OptionalTextInput
              value={value}
              onChange={v => onChange(v)}
              onEnter={() => submit()}
              onEsc={() => cancel()}
              focusOnMount
              maxLength={serverReference.constants.flight.maxTicketNumberLength}
            />,
            type: "confirmOrCancelButton",
            fullWidth: true,
            initialValue: state.eTicket,
            submitChange: (eTicket: string) => {
              return window.CoreApi.Extractor.UpdateExtractedFlightPassenger(
                getUpdateInstruction({ ...state, eTicket })
              )
            }
          }
        }}
      />
      <PassengerCardField
        gridArea="baggage"
        field={{
          key: "baggage",
          isEmpty: !state.baggage,
          label: RESX.Flight.Baggage,
          render: () => <OptionalTextAreaDetails>{state.baggage}</OptionalTextAreaDetails>,
          inPageEdit: {
            render: ({ value, onChange, submit, cancel }) => <OptionalTextAreaInput
              value={value}
              rows={2}
              onChange={v => onChange(v)}
              onCtrlEnter={() => submit()}
              onEsc={() => cancel()}
              focusOnMount
              maxLength={serverReference.constants.flight.maxBaggageLength}
            />,
            type: "confirmOrCancelButton",
            fullWidth: true,
            initialValue: state.baggage,
            submitChange: (baggage: string) => {
              return window.CoreApi.Extractor.UpdateExtractedFlightPassenger(
                getUpdateInstruction({ ...state, baggage })
              )
            }
          }
        }}
      />
      {(props.isInPageEditable && !isNarrow) && <div style={{ gridArea: "buttonDelete" }}>
        <PhosphorTrashButton
          layout="in-square"
          onClick={() => setIsDeleting(true)}
        />
      </div>}
      <DeleteDialog
        spinForever
        isOpen={isDeleting}
        onDelete={onDelete}
        handleClose={() => { setIsDeleting(false); }}
        message={RESX.GeneralWarnings.DeleteSure}
      />
    </div>
  </InPageEditingProvider>
}
